import {useFormik} from 'formik';
import * as Yup from 'yup';

import {Button, Input} from '@/components';
import CountryPicker from '@/components/CountryPicker';
import Modal from '@/components/Modals';
import type {BusinessOnboarding, BusinessOnboardingEditRequest} from '@/types';

interface EditInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  invite: BusinessOnboarding | null;
  onSubmit: (payload: BusinessOnboardingEditRequest) => Promise<void>;
  isLoading: boolean;
}

// Mirrors the server edit schema. Country shows only on invites from before the
// signup wizard, which carry one; newer ones collect it in the wizard.
const validationSchema = Yup.object({
  name: Yup.string().required('Full name is required'),
  businessName: Yup.string(),
  email: Yup.string()
    .email('Invalid email address')
    .required('Business email is required'),
  businessCountry: Yup.string(),
});

type FormValues = {
  name: string;
  businessName: string;
  email: string;
  businessCountry: string;
};

const EditInviteModal = ({
  isOpen,
  onClose,
  invite,
  onSubmit,
  isLoading,
}: EditInviteModalProps) => {
  const hasCountry = !!invite?.businessCountry;
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      name: invite?.name ?? '',
      businessName: invite?.businessName ?? '',
      email: invite?.email ?? '',
      businessCountry: invite?.businessCountry ?? '',
    },
    validationSchema,
    onSubmit: async (values) => {
      // Send only fields that actually changed, so we never overwrite with
      // unchanged values (and satisfy the server's "at least one field" rule).
      const payload: BusinessOnboardingEditRequest = {};
      if (values.name !== (invite?.name ?? '')) {
        payload.name = values.name;
      }
      if (values.businessName !== (invite?.businessName ?? '')) {
        payload.businessName = values.businessName;
      }
      if (values.email !== (invite?.email ?? '')) {
        payload.email = values.email;
      }
      if (
        hasCountry &&
        values.businessCountry !== (invite?.businessCountry ?? '')
      ) {
        payload.businessCountry =
          values.businessCountry as BusinessOnboardingEditRequest['businessCountry'];
      }
      await onSubmit(payload);
    },
  });

  const fieldError = (field: keyof FormValues) =>
    formik.touched[field] && formik.errors[field] ? (
      <p className="text-red-500 text-xs -mt-2">{formik.errors[field]}</p>
    ) : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="w-11/12 sm:w-5/12 md:w-5/12 lg:w-4/12 xl:w-3/12 px-4 sm:px-0"
      title="Edit invite"
      closeOnOutsideClick
    >
      <div className="pt-3 px-2">
        <form onSubmit={formik.handleSubmit} className="flex flex-col gap-3">
          <Input
            label="Full Name"
            placeholder="Contact's full name"
            value={formik.values.name}
            onChange={formik.handleChange('name')}
            onBlur={formik.handleBlur('name')}
            className="w-full"
          />
          {fieldError('name')}

          <Input
            label="Business Name"
            placeholder="Registered business name"
            value={formik.values.businessName}
            onChange={formik.handleChange('businessName')}
            onBlur={formik.handleBlur('businessName')}
            className="w-full"
          />
          {fieldError('businessName')}

          <Input
            label="Email"
            placeholder="yourname@example.com"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange('email')}
            onBlur={formik.handleBlur('email')}
            className="w-full"
          />
          {fieldError('email')}

          {hasCountry && (
            <>
              <CountryPicker
                label="Country"
                placeholder="Select country"
                value={formik.values.businessCountry}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  formik.setFieldValue('businessCountry', e.target.value)
                }
                onBlur={formik.handleBlur('businessCountry')}
                className="w-full"
              />
              {fieldError('businessCountry')}
            </>
          )}

          <div className="text-end pb-5 mt-2">
            <Button
              type="submit"
              variant="solid"
              colorScheme="cyan"
              disabled={!formik.isValid || !formik.dirty || isLoading}
              isLoading={isLoading}
              loadingText="Saving..."
            >
              Save changes
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EditInviteModal;
